import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Gfooter from "../Component/Gfooter";
import styles from "./BlogReadMore2.module.css";
/* ── Animation variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const heroImgVariant = {
  hidden: { scale: 1.12, opacity: 0 },
  show: { scale: 1, opacity: 1, transition: { duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const modalVariant = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalImgVariant = {
  hidden: { scale: 0.88, opacity: 0 },
  show: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { scale: 0.88, opacity: 0, transition: { duration: 0.25 } },
};

function BlogReadMore2() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [multiImages, setMultiImages] = useState([]);

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        const formData = new FormData();
        formData.append("id", id);
        const response = await axios.post(
          "https://cyberathon.com/NLF/index.php/Api/Nlf_Controller/get_by_id_blog_card",
          formData
        );
        if (response.data.status === true) {
          const blogData = response.data.data;
          setBlog(blogData);
          if (Array.isArray(blogData.multi_image)) {
            setMultiImages(blogData.multi_image);
          } else if (typeof blogData.multi_image === "string") {
            setMultiImages(blogData.multi_image.split(","));
          } else {
            setMultiImages([]);
          }
        }
      } catch (error) {
        console.error("Error fetching blog detail:", error);
      }
    };
    fetchBlogDetail();
  }, [id]);

  if (!blog) {
    return (
      <div className={styles.loading}>
        <motion.span
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          Loading…
        </motion.span>
      </div>
    );
  }

  const fullImageURLs = multiImages.map(
    (img) => `https://cyberathon.com/NLF/${img.trim()}`
  );

  const heroImage = blog.blog_image
    ? `https://cyberathon.com/NLF/${blog.blog_image}`
    : (fullImageURLs[0] || null);

  const handlePrev = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) =>
      prev === 0 ? fullImageURLs.length - 1 : prev - 1
    );
  };
  const handleNext = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) =>
      prev === fullImageURLs.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <>

      <Helmet>
        <title>{blog.title} | NLF Solutions</title>
        <link rel="canonical" href={`https://nlfs.in/blogRead/${id}`} />
      </Helmet>
      <div className={styles.page}>

        {/* ── HERO COVER ── */}
        <div className={styles.heroCover}>
          {heroImage && (
            <motion.img
              src={heroImage}
              alt={blog.title}
              className={styles.heroBg}
              variants={heroImgVariant}
              initial="hidden"
              animate="show"
            />
          )}
          <div className={styles.heroOverlay} />
          <div className={styles.heroRedBar} />

          <motion.div
            className={styles.heroContent}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0.3}
          >
            <div className={styles.heroCrumb}>
              <span>NLF</span>
              <span>›</span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>Blogs</span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>›</span>
              Article
            </div>
            <h1 className={styles.heroTitle}>{blog.title}</h1>
          </motion.div>
        </div>

        {/* ── ARTICLE ── */}
        <div className={styles.article}>

          {/* Description */}
          <motion.div
            className={styles.descBlock}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={0}
            dangerouslySetInnerHTML={{ __html: blog.description }}
          />

          {/* Gallery */}
          {fullImageURLs.length > 0 && (
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={1}
            >
              <div className={styles.galleryLabel}>
                <span>Gallery</span>
              </div>

              <div
                className={`${styles.imageGrid} ${fullImageURLs.length < 5 ? styles.fewImages : ""
                  }`}
              >
                {fullImageURLs.slice(0, 5).map((img, index) => (
                  <motion.div
                    key={index}
                    className={styles.imgWrapper}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    custom={index * 0.08}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={img} alt={`blog-${index}`} />
                    <div className={styles.imgOverlay}>
                      <div className={styles.expandIcon}>
                        <svg viewBox="0 0 24 24">
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Back button */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={2}
          >
            <Link to="/blogs" className={styles.backBtn}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Blogs
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ── LIGHTBOX MODAL ── */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            className={styles.modal}
            variants={modalVariant}
            initial="hidden"
            animate="show"
            exit="exit"
            onClick={() => setSelectedImageIndex(null)}
          >
            <div className={styles.modalInner} onClick={(e) => e.stopPropagation()}>

              {/* Counter */}
              <div className={styles.modalCounter}>
                {selectedImageIndex + 1} / {fullImageURLs.length}
              </div>

              {/* Close */}
              <button
                className={styles.modalClose}
                onClick={() => setSelectedImageIndex(null)}
              >
                ✕
              </button>

              {/* Left Arrow */}
              <button className={`${styles.arrowBtn} ${styles.arrowLeft}`} onClick={handlePrev}>
                ❮
              </button>

              {/* Image */}
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImageIndex}
                  src={fullImageURLs[selectedImageIndex]}
                  alt="expanded"
                  className={styles.modalImg}
                  variants={modalImgVariant}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                />
              </AnimatePresence>

              {/* Right Arrow */}
              <button className={`${styles.arrowBtn} ${styles.arrowRight}`} onClick={handleNext}>
                ❯
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Gfooter />
    </>
  );
}

export default BlogReadMore2;